import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
  Typography,
  Collapse,
  Chip,
  Avatar,
  Badge,
} from '@mui/material';
import {
  FaTachometerAlt,
  FaUserEdit,
  FaIdBadge,
  FaUserCircle,
  FaBuilding,
  FaChartLine,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaStar,
  FaUsers,
  FaCog,
  FaSignOutAlt,
} from 'react-icons/fa';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import logo from '../../assets/images/logo.png';

/**
 * Helper function to get user info from localStorage
 * @returns {Object} User information object
 */
const getUserInfo = () => {
  try {
    const storedProfile = localStorage.getItem('userProfile');
    if (!storedProfile) {
      return {
        name: 'HR Staff',
        role: 'HR',
        designation: 'Human Resources',
        hotelCode: 'N/A',
        department: 'HR',
        avatar: null,
      };
    }
    
    const profile = JSON.parse(storedProfile);
    return {
      name: profile.full_name || profile.first_name || 'HR Staff',
      role: profile.user_type || profile.role || 'HR',
      designation: profile.designation || profile.department || 'Human Resources',
      hotelCode: profile.hotel_code || 'N/A',
      department: profile.department || 'HR',
      avatar: profile.avatar || null,
    };
  } catch (error) {
    console.error('Error parsing user profile:', error);
    return {
      name: 'HR Staff',
      role: 'HR',
      designation: 'Human Resources',
      hotelCode: 'N/A',
      department: 'HR',
      avatar: null,
    };
  }
};

function Sidebar_HR() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();

  const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Elegantè Hotel';
  const SERVICE_PROVIDERS = process.env.REACT_APP_VENDOR_SERVICE_PROVIDERS || 'e-Neurons';

  const userInfo = getUserInfo();

  // Group menu items for HR
  const menuGroups = [
    {
      title: 'MAIN',
      items: [
        { text: 'Dashboard', icon: <FaTachometerAlt />, link: '/hotel/HR/Dashboard_hr' },
          ]
    },
    {
      title: 'EMPLOYEE MANAGEMENT',
      icon: <FaUsers />,
      items: [
        { text: 'Initialize Employee', icon: <FaUserCircle />, link: '/hotel/HR/StaffInitialization' },
        { text: 'Onboard Employee', icon: <FaUserCircle />, link: '/hotel/HR/Onboard' },
        { text: 'Employee Profile', icon: <FaIdBadge />, link: '/hotel/HR/ProfileCheckerPhone' },
        { text: 'Active & Inactive', icon: <FaBuilding />, link: '/hotel/HR/Dept' },
      ]
    },
    {
      title: 'PERFORMANCE',
      icon: <FaStar />,
      items: [
        { text: 'Appraisal', icon: <FaChartLine />, link: '/hotel/HR/StaffAppraisals' },
        { text: 'Quarterly Review', icon: <FaCalendarAlt />, link: '/hotel/HR/QuarterlyReviews' },
      ]
    },
    {
      title: 'LEAVE & ATTENDANCE',
      icon: <FaCalendarAlt />,
      items: [
        { text: 'Leaves', icon: <FaChartLine />, link: '/hotel/HR/HrLeaveDashboard' },
      ]
    },
    {
      title: 'COMPENSATION',
      icon: <FaMoneyBillWave />,
      items: [
        { text: 'Set/Update Salary', icon: <FaUserEdit />, link: '/hotel/HR/Set_salary' },
        { text: 'Salary Deductions', icon: <FaUserEdit />, link: '/hotel/HR/SalaryDeductions' },
      ]
    },
    {
      title: 'REPORTS',
      icon: <FaFileAlt />,
      items: [
        { text: 'Reports', icon: <FaChartLine />, link: '/hotel/HR/HrReportsDashboard' },
      ]
    }
  ];

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  
  const handleMenuItemClick = () => {
    if (window.innerWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const toggleMenuGroup = (groupTitle) => {
    setOpenMenus(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  // Check if any item in a group is active
  const isGroupActive = (group) => {
    return group.items.some(item => location.pathname === item.link);
  };

  // Auto-expand groups that contain the active link
  useEffect(() => {
    const newOpenMenus = { ...openMenus };
    menuGroups.forEach(group => {
      const hasActive = group.items.some(item => location.pathname === item.link);
      if (hasActive && !newOpenMenus[group.title]) {
        newOpenMenus[group.title] = true;
      }
    });
    setOpenMenus(newOpenMenus);
  }, [location.pathname]);

  const renderMenuItem = (item) => {
    const isSelected = location.pathname === item.link;
    return (
      <ListItem
        button
        component={Link}
        to={item.link}
        key={item.text}
        onClick={handleMenuItemClick}
        sx={{
          backgroundColor: isSelected ? '#37474F' : 'transparent',
          boxShadow: isSelected ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : 'none',
          border: isSelected ? '1px solid #455A64' : 'none',
          borderRadius: '12px',
          paddingY: 1.5,
          paddingX: 2,
          marginY: 0.5,
          marginX: 1,
          color: '#ECEFF1',
          '&:hover': {
            backgroundColor: '#455A64',
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.4)',
          },
          pl: 4,
        }}
      >
        <Box sx={{ marginRight: 2, fontSize: '1.2rem' }}>{item.icon}</Box>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: '0.9rem',
            fontWeight: isSelected ? 600 : 400,
          }}
        />
      </ListItem>
    );
  };

  const renderMenuGroup = (group) => {
    const isOpen = openMenus[group.title];
    const isActive = isGroupActive(group);
    
    return (
      <Box key={group.title}>
        <ListItem
          button
          onClick={() => toggleMenuGroup(group.title)}
          sx={{
            backgroundColor: isActive ? '#37474F' : '#263238',
            borderRadius: '12px',
            paddingY: 1.5,
            paddingX: 2,
            marginY: 0.5,
            marginX: 1,
            color: '#ECEFF1',
            '&:hover': {
              backgroundColor: '#455A64',
            },
          }}
        >
          <Box sx={{ marginRight: 2, fontSize: '1.2rem' }}>{group.icon}</Box>
          <ListItemText
            primary={group.title}
            primaryTypographyProps={{
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          />
          {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {group.items.map(item => renderMenuItem(item))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderMenuItems = () => (
    <List>
      {menuGroups.map((group) => {
        // For MAIN group (no dropdown)
        if (group.title === 'MAIN') {
          return group.items.map(item => (
            <ListItem
              button
              component={Link}
              to={item.link}
              key={item.text}
              onClick={handleMenuItemClick}
              sx={{
                backgroundColor: location.pathname === item.link ? '#37474F' : '#263238',
                boxShadow: location.pathname === item.link ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : '0px 2px 6px rgba(0, 0, 0, 0.25)',
                border: '1px solid #455A64',
                borderRadius: '12px',
                paddingY: 2,
                paddingX: 2,
                marginY: 1.2,
                marginX: 1,
                color: '#ECEFF1',
                '&:hover': {
                  backgroundColor: '#455A64',
                  boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.4)',
                },
              }}
            >
              <Box sx={{ marginRight: 2, fontSize: '1.5rem' }}>{item.icon}</Box>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              />
            </ListItem>
          ));
        }
        return renderMenuGroup(group);
      })}
    </List>
  );

  // Desktop sidebar content
  const desktopSidebar = (
    <Box
      sx={{
        width: 280,
        height: '100vh',
        backgroundColor: '#1a1a2e',
        display: 'flex',
        flexDirection: 'column',
        color: 'white',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#16213e',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#0f3460',
          borderRadius: '4px',
        },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#0f3460',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          mb: 2,
          gap: 2,
        }}
      >
        <img
          src={logo}
          alt="Logo"
          style={{
            width: 50,
            height: 50,
            borderRadius: '10px',
          }}
        />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
            {HOTEL_NAME}
          </Typography>
          <Typography variant="caption" sx={{ color: '#a0a0a0', fontSize: '0.7rem' }}>
            HR Portal
          </Typography>
        </Box>
      </Box>

      {/* User Profile Section */}
      <Box sx={{ px: 2, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          p: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #16213e 0%, #0f3460 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            color="success"
          >
            <Avatar sx={{ 
              width: 50, 
              height: 50, 
              backgroundColor: '#e94560',
              boxShadow: '0 4px 12px rgba(233, 69, 96, 0.3)',
            }}>
              {userInfo.name.charAt(0)}
            </Avatar>
          </Badge>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
              {userInfo.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#e94560', fontWeight: 600 }}>
              {userInfo.role}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#a0a0a0', fontSize: '0.7rem' }}>
              Code: {userInfo.hotelCode}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      {renderMenuItems()}
      
      {/* Footer */}
      <Box sx={{ mt: 'auto', p: 2, textAlign: 'center', borderTop: '1px solid #16213e' }}>
        <Typography variant="caption" sx={{ color: '#a0a0a0', fontSize: '0.7rem' }}>
          © Elegantè HRMS
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#6c6c6c', fontSize: '0.65rem', mt: 0.5 }}>
          Powered by {SERVICE_PROVIDERS}
        </Typography>
      </Box>
    </Box>
  );

  // Mobile drawer content
  const mobileDrawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        backgroundColor: '#1a1a2e',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          textAlign: 'center',
          paddingY: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          backgroundColor: '#0f3460',
        }}
      >
        <img src={logo} alt="Logo" style={{ width: 40, height: 40 }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
          {HOTEL_NAME}
        </Typography>
      </Box>
      
      {/* User Profile */}
      <Box sx={{ p: 2, borderBottom: '1px solid #16213e' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, backgroundColor: '#e94560' }}>
            {userInfo.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {userInfo.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#e94560' }}>
              {userInfo.role}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ borderColor: '#16213e' }} />
      {renderMenuItems()}
    </Box>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1301,
          display: { xs: 'block', md: 'none' },
        }}
      >
        <IconButton 
          onClick={toggleDrawer}
          sx={{
            backgroundColor: '#0f3460',
            '&:hover': { backgroundColor: '#e94560' },
          }}
        >
          <MenuIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>

      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'transparent',
            border: 'none',
          },
        }}
      >
        {mobileDrawer}
      </Drawer>

      {/* Sidebar for desktop */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexShrink: 0,
        }}
      >
        {desktopSidebar}
      </Box>
    </>
  );
}

export default Sidebar_HR;